/**
 * Every number the chart writes, written one way: as the SSRS report it replaces wrote it, in Irish
 * English.
 *
 * Until 13/10/26 the chart had four ways of writing a number. Bar labels followed the report and the
 * browser's locale, the tiles rounded everything to a whole number in US English, the column totals
 * and the hover panel rounded with Math.round, and the pie had a rule of its own. So a power factor of
 * 0.93 read "1" on its tile, 3.46 Tonnes CO2 read "3", -0.3 read "-0", and a German browser wrote
 * "1.234,5" on a bar beside "1,235" on a tile.
 *
 * The decimals are each report's Code.FormatNumber, read off the RDLs:
 *
 *   remainder, Compare     n0
 *   policy, status         n2 under 10, then n0
 *   policy breakdown       n2 under 1, then n0
 *   consumption            n2 under 2, n1 under 10, then n0
 *
 * An average (a temperature, a power factor) keeps two decimals under 10 and one under 100 whatever
 * the report, since a whole number says nothing about a power factor.
 */

/** Which SSRS report a chart stands in for, by the decimals it writes. */
export type ReportKind = 'remainder' | 'policy' | 'breakdown' | 'consumption' | 'status' | 'compare';

/** The report kind for a category's report URL. Anything unrecognised is the remainder, as the API treats it. */
export function reportKind(reportUrl: string | null | undefined, compare = false): ReportKind {
  if (compare) return 'compare';
  const r = (reportUrl || '').toLowerCase();
  if (r.startsWith('rptresourcepolicybreakdown')) return 'breakdown';
  if (r.startsWith('rptresourcepolicy')) return 'policy';
  if (r === 'rptresourceconsumption') return 'consumption';
  if (r === 'rptresourcestatus') return 'status';
  return 'remainder';
}

/** Decimal places for a figure of this size on this report. */
export function placesFor(kind: ReportKind, value: number, averaged = false): number {
  const a = Math.abs(value);
  if (averaged) return a < 10 ? 2 : a < 100 ? 1 : 0;
  switch (kind) {
    case 'policy':
    case 'status': return a < 10 ? 2 : 0;
    // SSRS tested the signed value here, so every negative got two decimals ("-5.00"). Taken as the
    // slip it is: a negative is sized like a positive.
    case 'breakdown': return a < 1 ? 2 : 0;
    case 'consumption': return a < 2 ? 2 : a < 10 ? 1 : 0;
    default: return 0;
  }
}

/** Whether a point gets its number on the bars, by the report's own rule (each RDL's Visible). */
export function labelShown(kind: ReportKind, value: number): boolean {
  // Policy labels only positive points over 1; every other report labels a point more than 1 either way.
  return kind === 'policy' ? value > 1 : Math.abs(value) > 1;
}

/** The locale every figure and date is written in, as SSRS's aCultureInfo was. */
export const LOCALE = 'en-IE';

const formats = new Map<number, Intl.NumberFormat>();

/**
 * A number to a fixed count of decimals, grouped in thousands. Halves round away from zero, as .NET's
 * n0 and Intl both do, and a figure that rounds to nothing is written "0", never "-0".
 */
export function formatNumber(value: number | null | undefined, places = 0): string {
  if (value == null || !isFinite(value)) return '';
  let f = formats.get(places);
  if (!f) {
    f = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: places, maximumFractionDigits: places });
    formats.set(places, f);
  }
  const text = f.format(value);
  return /^-0(?:\.0+)?$/.test(text) ? text.slice(1) : text;
}

/** A figure as its report writes it. */
export function formatValue(value: number | null | undefined, kind: ReportKind, averaged = false): string {
  if (value == null || !isFinite(value)) return '';
  return formatNumber(value, placesFor(kind, value, averaged));
}

/** A figure as its report writes it, with the unit after it where there is one. */
export function formatWithUnit(value: number | null | undefined, unit: string | null | undefined, kind: ReportKind, averaged = false): string {
  const text = formatValue(value, kind, averaged);
  return text && unit ? `${text} ${unit}` : text;
}

/** A share of a whole, 0.374 as "37%". */
export function formatPercent(share: number | null | undefined, places = 0): string {
  if (share == null || !isFinite(share)) return '';
  return formatNumber(share * 100, places) + '%';
}
